import { useEffect, useState } from "react";

/**
 * Subscribe to a CSS media query from JavaScript.
 *
 * Layout a breakpoint can express belongs in a Tailwind class, not here. This
 * is for the cases a class cannot reach, where the *behaviour* differs rather
 * than the styling: the ticket details panel is a column that starts open on a
 * desktop and a sheet that starts closed on a phone, and no amount of CSS turns
 * one default into the other.
 */
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() => matchesNow(query));

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;

    const list = window.matchMedia(query);
    const onChange = (event: MediaQueryListEvent): void =>
      setMatches(event.matches);

    // Re-read on subscribe rather than trusting the initial state: the viewport
    // can have changed between first render and this effect running.
    setMatches(list.matches);

    list.addEventListener("change", onChange);
    return () => list.removeEventListener("change", onChange);
  }, [query]);

  return matches;
}

function matchesNow(query: string): boolean {
  // Guarded so a test environment without matchMedia does not throw. Falling
  // through to false is the mobile-first answer, which is the safe one: a phone
  // layout on a desktop is usable, the reverse is not.
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return false;
  }
  return window.matchMedia(query).matches;
}

/**
 * Tailwind's `md`, which is where the three-pane layout starts to fit.
 *
 * Kept as a constant so the JavaScript and the `md:` classes can never drift
 * apart -- a hook that disagreed with the stylesheet by a pixel would hide a
 * panel the CSS was still reserving space for.
 */
export const DESKTOP_QUERY = "(min-width: 48rem)";

export function useIsDesktop(): boolean {
  return useMediaQuery(DESKTOP_QUERY);
}

/**
 * A value that lags behind its input until it stops changing.
 *
 * For search boxes: the query is what the agent has finished typing, not what
 * they are part-way through, and firing a request per keystroke both floods the
 * API and makes the list flicker through nonsense intermediate results.
 */
export function useDebounced<T>(value: T, delayMs: number): T {
  const [settled, setSettled] = useState(value);

  useEffect(() => {
    const timer = window.setTimeout(() => setSettled(value), delayMs);
    return () => window.clearTimeout(timer);
  }, [value, delayMs]);

  return settled;
}
